import { InlineBanner, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { SportIcon } from "../onboarding/sportIcons";
import { ContestTypeIcon } from "./contestTypeIcons";
import type {
  typeCreatePoolDraft,
  typeCreatePoolLeagueOption,
  typePoolTypeRow,
} from "./createPoolDraft";
import { CreatePoolTileGrid } from "./CreatePoolTileGrid";

export type typeCreatePoolLeagueStepProps = {
  draft: typeCreatePoolDraft;
  leagueOptions: readonly typeCreatePoolLeagueOption[];
  poolTypeRows: readonly typePoolTypeRow[];
  isLoading: boolean;
  hasFailed: boolean;
  onRetry: () => void;
  onChooseLeague: (option: typeCreatePoolLeagueOption) => void;
  onChooseContestType: (contestType: string) => void;
};

/**
 * Step 1: which league and what contest type, as two tile grids (ISSUE-1092).
 * There is no season control: the server resolves the league's current
 * season, or the next one, and the review step names it.
 *
 * There is no "no league is open" branch here. ISSUE-782 / Q-782-A answered
 * that with option B: entry to the wizard is closed on the pools screen, where
 * Create pool renders disabled with the reason beside it, so a fan is never
 * inside this step with nothing to pick. `routes/pools.tsx` holds that gate for
 * the button and for `?dialog=create-pool` alike.
 */
export const CreatePoolLeagueStep = (
  props: typeCreatePoolLeagueStepProps,
): ReactNode => {
  if (props.hasFailed) {
    return (
      <div data-testid="create-pool-catalog-failed">
        <InlineBanner
          variant="warning"
          message="We could not load the leagues."
          action={{ label: "Try again", onAction: props.onRetry }}
        />
      </div>
    );
  }

  if (props.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton shape="control" label="Loading the leagues" />
        <Skeleton shape="control" />
        <Skeleton shape="control" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="create-pool-step-league">
      <CreatePoolTileGrid
        label="Which league?"
        testId="create-pool-league-tiles"
        tiles={props.leagueOptions.map((option) => ({
          value: option.leagueKey,
          title: option.shortName,
          subtitle: option.label,
          icon: <SportIcon sportKey={option.sportKey} />,
          isDisabled: false,
        }))}
        value={props.draft.leagueKey}
        onValueChange={(next) => {
          const match = props.leagueOptions.find(
            (option) => option.leagueKey === next,
          );
          if (match !== undefined) props.onChooseLeague(match);
        }}
      />
      <CreatePoolTileGrid
        label="What are you playing?"
        testId="create-pool-contest-tiles"
        tiles={props.poolTypeRows.map((row) => ({
          value: row.contestType,
          title: row.label,
          subtitle: row.description,
          icon: <ContestTypeIcon contestType={row.contestType} />,
          isDisabled: !row.isAvailable,
        }))}
        value={props.draft.contestType}
        onValueChange={props.onChooseContestType}
      />
      <p className="text-caption text-ink-secondary">
        League and contest type are locked once the pool is created.
      </p>
    </div>
  );
};
